import * as React from 'react';
import * as d3 from 'd3';
import { observer } from 'mobx-react';
import { IDispersionControlProps } from './IDispersionControlProps';
import { IReportMeasurementGroup, IReportMeasurement, ICoordinate2D } from 'Data';
import { Parameter } from 'MeasurementsCalculations';
import { IReportDataService } from 'Services';
import { DispersionBackground } from './DispersionBackground/DispersionBackground';
import { DispersionElipse } from './DispersionElipse/DispersoinElipse';
import { IDispersionDotProps } from './DispersionDot/IDispersionDotProps';
import { DispersionDot } from './DispersionDot/DispersionDot';

interface IGraphHeightWidth {
  furthestHorizontallyPoint: number;
  furthestVerticallyPoint: number;
}

@observer
export class DispersionControl extends React.Component<IDispersionControlProps> {
  private readonly reportDataService: IReportDataService;
  private counter: number = 0;

  constructor(props: IDispersionControlProps) {
    super(props, undefined);
    this.reportDataService = this.props.multiShotReportDataService;
    this.handleBackgroundClick = this.handleBackgroundClick.bind(this);
  }

  private isEnoughDataToShowDispersion(graphWidthHeigth: IGraphHeightWidth): boolean {
    return graphWidthHeigth.furthestHorizontallyPoint !== 0;
  }

  private hasSelectedStrokeEnoughData(): boolean {
    let stroke;
    if (this.props.selectedStroke) stroke = this.props.selectedStroke;
    else if (this.props.selectedAverage) stroke = this.props.selectedAverage;

    if (!stroke) return false;

    if (
      (stroke.total !== null && stroke.sideTotal !== null) ||
      (stroke.carry !== null && stroke.total !== null)
    )
      return true;

    return false;
  }

  private getDispersionCoordinates(stroke: IReportMeasurement, dispersionMode: string) {
    //toodo: introduce enumerable
    if (dispersionMode === 'Carry') {
      return {
        XParameter: Parameter.Carry,
        XParameterValue: stroke.carry,
        YParameter: Parameter.Carry, // This should be Parameter.Carry (not Parameter.Side) in order to keep the unit the same on both axes (especially in case of yards/feet)
        YParameterValue: stroke.side,
      };
    } else {
      return {
        XParameter: Parameter.Total,
        XParameterValue: stroke.total,
        YParameter: Parameter.Total, // This should be Parameter.Total (not Parameter.SideTotal) in order to keep the unit the same on both axes (especially in case of yards/feet)
        YParameterValue: stroke.sideTotal,
      };
    }
  }

  private calculateMaximumWidthAndHeightOfTheGraph(): IGraphHeightWidth {
    let furthestVerticallyPointCarry = 0;
    let furthestHorizontallyPointCarry = 0;
    let furthestVerticallyPointTotal = 0;
    let furthestHorizontallyPointTotal = 0;
    const measurementsData = this.props.measurementsData;
    const unitPresentation = this.props.unitPresentation;

    for (let i = 0; i < measurementsData.length; i++) {
      if (measurementsData[i].isSelected) {
        let s = measurementsData[i].strokes ?? [];
        for (let j = 0; j < s.length; j++) {
          if (measurementsData[i].isSelected) {
            const dispersionCoordinateTotal = this.getDispersionCoordinates(s[j], 'Total');
            const dispersionCoordinateCarry = this.getDispersionCoordinates(s[j], 'Carry');

            //Furhtest points according to total
            let pointX = unitPresentation.value(
              dispersionCoordinateTotal.XParameter,
              dispersionCoordinateTotal.XParameterValue
            );
            if (furthestHorizontallyPointTotal < pointX) {
              furthestHorizontallyPointTotal = unitPresentation.value(
                dispersionCoordinateTotal.XParameter,
                dispersionCoordinateTotal.XParameterValue
              );
            }
            if (
              furthestVerticallyPointTotal <
              Math.abs(
                unitPresentation.value(
                  dispersionCoordinateTotal.YParameter,
                  dispersionCoordinateTotal.YParameterValue
                )
              )
            ) {
              furthestVerticallyPointTotal = Math.abs(
                unitPresentation.value(
                  dispersionCoordinateTotal.YParameter,
                  dispersionCoordinateTotal.YParameterValue
                )
              );
            }

            //Furhtest points according to carry
            if (
              furthestHorizontallyPointCarry <
              unitPresentation.value(
                dispersionCoordinateCarry.XParameter,
                dispersionCoordinateCarry.XParameterValue
              )
            ) {
              furthestHorizontallyPointCarry = unitPresentation.value(
                dispersionCoordinateCarry.XParameter,
                dispersionCoordinateCarry.XParameterValue
              );
            }
            if (
              furthestVerticallyPointCarry <
              Math.abs(
                unitPresentation.value(
                  dispersionCoordinateCarry.YParameter,
                  dispersionCoordinateCarry.YParameterValue
                )
              )
            ) {
              furthestVerticallyPointCarry = Math.abs(
                unitPresentation.value(
                  dispersionCoordinateCarry.YParameter,
                  dispersionCoordinateCarry.YParameterValue
                )
              );
            }
          }
        }
      }
    }

    const marginCorrectionCooficient = 1.05;
    return {
      furthestHorizontallyPoint:
        Math.max(furthestHorizontallyPointCarry, furthestHorizontallyPointTotal) *
        marginCorrectionCooficient,
      furthestVerticallyPoint:
        Math.max(furthestVerticallyPointCarry, furthestVerticallyPointTotal) *
        marginCorrectionCooficient,
    };
  }

  private calculateXPointOnArc(y: number, radius: number) {
    return Math.cos(Math.asin(y / radius)) * radius;
  }

  private selectDispersionMode(
    dispersionMode: string,
    event: React.MouseEvent<HTMLDivElement>
  ): void {
    this.props.onDispersionModeChange(dispersionMode);
  }

  private showOnlyOneShot(stroke: IReportMeasurement, strokeGroup: IReportMeasurementGroup) {
    if (!this.props.selectedStroke || this.props.selectedStroke.id !== stroke.id) {
      this.props.onSelectedClubChange(stroke.club ?? '');
      this.props.onSelectedStrokeChange(stroke);
      this.props.onSelectedGroupOfStrokesChange(strokeGroup);
      this.props.onSelectedAverageChange(undefined);
    } else {
      this.handleBackgroundClick();
    }
  }

  private handleBackgroundClick(): void {
    this.props.onSelectedClubChange('');
    this.props.onSelectedStrokeChange(undefined);
    this.props.onSelectedGroupOfStrokesChange(undefined);
  }

  private isStrokeSelected() {
    return this.props.selectedStroke || this.props.selectedAverage;
  }

  render() {
    //Margins
    const marginTop = 20;
    const marginBottom = 20;
    const marginRight = 0;
    const marginLeft = 0;

    //SVG dimensions
    const svgWidthWithMargins = 1140;
    const svgHeightWithMargins = 382;

    const margin = { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft },
      width = svgWidthWithMargins - margin.left - margin.right,
      height = svgHeightWithMargins - margin.top - margin.bottom;

    const graphHeightWidth = this.calculateMaximumWidthAndHeightOfTheGraph();

    var furthestHorizontalBasedOnYAxis =
      ((graphHeightWidth.furthestVerticallyPoint * 2) / height) * width;
    if (furthestHorizontalBasedOnYAxis > graphHeightWidth.furthestHorizontallyPoint) {
      graphHeightWidth.furthestHorizontallyPoint = furthestHorizontalBasedOnYAxis;
    } else {
      var furthestVerticalBasedOnXAxis =
        ((graphHeightWidth.furthestHorizontallyPoint / width) * height) / 2;
      if (furthestVerticalBasedOnXAxis > graphHeightWidth.furthestVerticallyPoint) {
        graphHeightWidth.furthestVerticallyPoint = furthestVerticalBasedOnXAxis;
      }
    }

    //Scale for Axis
    const paddingForGraphInPixels = 0;
    const paddingForEllipsis = 15;
    const xAxisScale = d3.scale
      .linear()
      .domain([0, graphHeightWidth.furthestHorizontallyPoint])
      .range([paddingForGraphInPixels, width - paddingForGraphInPixels]);
    const yAxisScale = d3.scale
      .linear()
      .domain([-graphHeightWidth.furthestVerticallyPoint, graphHeightWidth.furthestVerticallyPoint])
      .range([paddingForGraphInPixels, height - paddingForGraphInPixels]);
    const xAxisScaleForEllipsis = d3.scale
      .linear()
      .domain([0, graphHeightWidth.furthestHorizontallyPoint])
      .range([paddingForEllipsis, width - paddingForEllipsis]);
    const yAxisScaleForEllipsis = d3.scale
      .linear()
      .domain([0, graphHeightWidth.furthestVerticallyPoint])
      .range([paddingForEllipsis, (height - paddingForEllipsis) / 2]);

    //Generate points for dispersion view
    let normalizedMeasurementsData = this.props.measurementsData;
    let unitPresentation = this.props.unitPresentation;
    let dispersionCoordinatesGroups: Array<{
      dispersionCoordinates: Array<ICoordinate2D>;
      dotsColors: string;
      groupId: string;
    }> = [];
    let dipserionsDotsProps: Array<IDispersionDotProps> = [];
    let selectedDotProps = null;

    if (this.props.selectedStrokesGroups.length > 0) {
      for (var a = 0; a < normalizedMeasurementsData.length; a++) {
        if (normalizedMeasurementsData[a]['isSelected']) {
          let dotsColors = normalizedMeasurementsData[a].color;
          var dispersionCoordinates: Array<ICoordinate2D> = [];
          let groupId = normalizedMeasurementsData[a].id;
          let strokes = normalizedMeasurementsData[a].strokes ?? [];
          for (var b = 0; b < strokes.length; b++) {
            let stroke = strokes[b];
            let groupOfStrokes = normalizedMeasurementsData[a];
            // we do this to capture the stroke and the strokeGroup variable
            // if we dont do this, when clicking, 'stroke' and 'strokeGroup' will always be the last element of the array
            let onClickFunction = ((capturedStroke, capturedGroupOfStrokes) => () => {
              this.showOnlyOneShot(capturedStroke, capturedGroupOfStrokes);
            })(stroke, groupOfStrokes);

            var dispersionCoordinate = this.getDispersionCoordinates(
              stroke,
              this.props.selectedDispersionMode
            );

            if (
              stroke.isVisible &&
              dispersionCoordinate.XParameterValue != null &&
              dispersionCoordinate.YParameterValue != null
            ) {
              var x = unitPresentation.value(
                dispersionCoordinate.XParameter,
                dispersionCoordinate.XParameterValue
              );
              var y = unitPresentation.value(
                dispersionCoordinate.YParameter,
                dispersionCoordinate.YParameterValue
              );
              x = this.calculateXPointOnArc(y, x);
              dispersionCoordinates.push({ X: x, Y: y });
              let dipserionsDotProps: IDispersionDotProps = {
                cx: xAxisScale(x),
                cy: yAxisScale(y),
                dotColor: dotsColors,
                className: '',
                onDotClick: onClickFunction,
                highlighted: true,
                dotId: stroke.id,
              };
              if (
                stroke &&
                this.props.selectedStroke &&
                stroke.id === this.props.selectedStroke.id
              ) {
                dipserionsDotProps.className = 'selected-circle';
                selectedDotProps = dipserionsDotProps;
              } else if (
                (this.props.selectedStroke || this.props.selectedAverage) &&
                this.props.selectedGroupOfStrokes &&
                this.props.selectedGroupOfStrokes.id !== groupOfStrokes.id
              ) {
                dipserionsDotProps.className = 'circle-not-selected';
                dipserionsDotsProps.push(dipserionsDotProps);
              } else {
                dipserionsDotProps.highlighted = false;
                dipserionsDotsProps.push(dipserionsDotProps);
              }
            }
          }
          let dc = dotsColors ? dotsColors : '';
          dispersionCoordinatesGroups.push({
            dispersionCoordinates,
            dotsColors: dc,
            groupId,
          });
        }
      }
    }

    selectedDotProps && dipserionsDotsProps.push(selectedDotProps);

    return (
      <div>
        {this.props.selectedStrokesGroups.length === 0 && (
          <div className="info-container">
            <p>You have no groups selected.</p>
            <p>Select a group and a shot to activate the dispersion view.</p>
          </div>
        )}
        {this.isEnoughDataToShowDispersion(graphHeightWidth) === false && (
          <div className="info-container">
            <p>Shots in the report do not have enough data</p>
            <p> to show the dispersion view.</p>
          </div>
        )}
        {this.isStrokeSelected() &&
          !this.hasSelectedStrokeEnoughData() &&
          this.isEnoughDataToShowDispersion(graphHeightWidth) && (
            <div className="info-container">
              <p>This shot does not have enough data.</p>
              <p>Choose another shot to show the dispersion view.</p>
            </div>
          )}
        <div className="carry-or-total-switch">
          <div className="view-text-container">
            <span>View</span>
          </div>
          <div className="selected-dispersion-mode-btns">
            <div
              className={
                this.props.selectedDispersionMode === 'Carry'
                  ? 'selected-dispersion-mode'
                  : undefined
              }
              onClick={event => this.selectDispersionMode('Carry', event)}
            >
              Carry
            </div>
            <div
              className={
                this.props.selectedDispersionMode === 'Total'
                  ? 'selected-dispersion-mode'
                  : undefined
              }
              onClick={event => this.selectDispersionMode('Total', event)}
            >
              Total
            </div>
          </div>
        </div>
        <DispersionBackground
          height={height}
          margins={margin}
          onBackgroundClick={this.handleBackgroundClick}
          width={width}
          xAxisScale={xAxisScale}
          yAxisScale={yAxisScale}
        >
          <svg className="background-rectangle-container" width={width} height={height}>
            {' '}
            {dispersionCoordinatesGroups.map(group => (
              <DispersionElipse
                key={group.groupId}
                dispersionCoordinates={group.dispersionCoordinates}
                elipseColor={group.dotsColors}
                reportDataService={this.reportDataService}
                xAxisScale={xAxisScale}
                xAxisScaleForEllipsis={xAxisScaleForEllipsis}
                yAxisScale={yAxisScale}
                yAxisScaleForEllipsis={yAxisScaleForEllipsis}
              />
            ))}
          </svg>
          <svg className="ball-container" width={width} height={height}>
            {' '}
            {dipserionsDotsProps.map(dotProps => (
              <DispersionDot key={dotProps.dotId} {...dotProps} />
            ))}
          </svg>
        </DispersionBackground>
      </div>
    );
  }
}
